import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';

export interface SortParagraphOption {
  id: number;
  blocks?: Array<{ type?: string; textContent?: string }>;
  textContent?: string;
}

export interface SortParagraphsPlayerProps {
  questionId: number;
  options: SortParagraphOption[];
  order: number[];
  onOrderChange: (order: number[]) => void;
  alignment?: 'ltr' | 'rtl';
  disabled?: boolean;
  hint?: string;
}

const LAYOUT_SPRING = { type: 'spring' as const, stiffness: 350, damping: 30 };

function stripHtml(html: string): string {
  if (!html) return '';
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
}

function extractParagraphText(option: SortParagraphOption, fallbackIndex: number): string {
  const block = option?.blocks?.find((b) => b.type === 'text' || b.textContent);
  if (block?.textContent) return stripHtml(block.textContent) || `Paragraph ${fallbackIndex + 1}`;
  if (option?.textContent) return stripHtml(option.textContent) || `Paragraph ${fallbackIndex + 1}`;
  return `Paragraph ${fallbackIndex + 1}`;
}

function reorderIds(base: number[], fromId: number, toIndex: number): number[] | null {
  const from = base.indexOf(fromId);
  if (from < 0 || toIndex < 0 || toIndex >= base.length || from === toIndex) return null;
  const next = [...base];
  const [item] = next.splice(from, 1);
  if (item === undefined) return null;
  next.splice(toIndex, 0, item);
  return next;
}

function dropIndexFromPoint(
  clientX: number,
  clientY: number,
  questionId: number,
  draggingId: number | null
): number | null {
  const el = document.elementFromPoint(clientX, clientY);
  const card = el?.closest?.(`[data-sort-paragraph-qid="${questionId}"]`) as HTMLElement | null;
  if (!card) return null;
  const optionId = Number(card.dataset.sortParagraphOptionId);
  if (draggingId != null && optionId === draggingId) return null;
  const idx = Number(card.dataset.sortParagraphIndex);
  return Number.isFinite(idx) ? idx : null;
}

export function SortParagraphsPlayer({
  questionId,
  options,
  order,
  onOrderChange,
  alignment = 'rtl',
  disabled,
  hint,
}: SortParagraphsPlayerProps) {
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [previewOrder, setPreviewOrder] = useState<number[] | null>(null);
  const [touchGhost, setTouchGhost] = useState<{
    optionId: number;
    clientX: number;
    clientY: number;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    text: string;
  } | null>(null);

  const committedOrderRef = useRef<number[]>([]);
  const draggingIdRef = useRef<number | null>(null);
  const previewIndexRef = useRef<number | null>(null);
  const pointerDraggingRef = useRef(false);

  const optionMap = new Map(options.map((o) => [o.id, o]));
  const committedOrder =
    order.length > 0
      ? order
      : [...options]
          .map((o) => o.id)
          .filter((id) => typeof id === 'number');

  committedOrderRef.current = committedOrder;

  const visualOrder = previewOrder ?? committedOrder;

  const commitMove = useCallback(
    (fromId: number, toIndex: number) => {
      if (disabled) return;
      const next = reorderIds(committedOrderRef.current, fromId, toIndex);
      if (!next) return;
      onOrderChange(next);
    },
    [disabled, onOrderChange]
  );

  const setPreviewToIndex = useCallback((toIndex: number | null) => {
    const fromId = draggingIdRef.current;
    if (fromId == null || toIndex == null) {
      previewIndexRef.current = null;
      setPreviewOrder(null);
      return;
    }
    previewIndexRef.current = toIndex;
    const next = reorderIds(committedOrderRef.current, fromId, toIndex);
    setPreviewOrder(next ?? committedOrderRef.current);
  }, []);

  const moveBy = (optionId: number, delta: number) => {
    if (disabled || draggingId != null) return;
    const index = committedOrder.indexOf(optionId);
    if (index < 0) return;
    const to = index + delta;
    if (to < 0 || to >= committedOrder.length) return;
    commitMove(optionId, to);
  };

  const endDrag = useCallback(
    (clientX?: number, clientY?: number) => {
      const fromId = draggingIdRef.current;
      let toIndex = previewIndexRef.current;
      if (fromId != null && toIndex == null && clientX != null && clientY != null) {
        toIndex = dropIndexFromPoint(clientX, clientY, questionId, fromId);
      }
      pointerDraggingRef.current = false;
      draggingIdRef.current = null;
      previewIndexRef.current = null;
      setTouchGhost(null);
      setDraggingId(null);
      setPreviewOrder(null);
      if (fromId != null && toIndex != null) {
        commitMove(fromId, toIndex);
      }
    },
    [commitMove, questionId]
  );

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!pointerDraggingRef.current || draggingIdRef.current == null) return;
      setTouchGhost((prev) =>
        prev ? { ...prev, clientX: e.clientX, clientY: e.clientY } : prev
      );
      const over = dropIndexFromPoint(e.clientX, e.clientY, questionId, draggingIdRef.current);
      if (over != null && over !== previewIndexRef.current) {
        setPreviewToIndex(over);
      }
    };
    const onUp = (e: PointerEvent) => {
      if (!pointerDraggingRef.current) return;
      endDrag(e.clientX, e.clientY);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [endDrag, questionId, setPreviewToIndex]);

  useEffect(() => {
    setPreviewOrder(null);
    previewIndexRef.current = null;
  }, [order]);

  return (
    <div className="space-y-2">
      {hint ? (
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1" dir="auto">
          {hint}
        </p>
      ) : null}
      <motion.div
        className="flex flex-col gap-2"
        layout
        transition={LAYOUT_SPRING}
        dir={alignment}
      >
        <AnimatePresence initial={false}>
          {visualOrder.map((optionId, idx) => {
            const option = optionMap.get(optionId);
            if (!option) return null;
            const text = extractParagraphText(option, idx);
            const isDragging = draggingId === optionId;
            const isTouchGhosted = touchGhost?.optionId === optionId;

            return (
              <motion.div
                key={optionId}
                layout
                transition={LAYOUT_SPRING}
                initial={false}
                data-sort-paragraph-qid={questionId}
                data-sort-paragraph-index={idx}
                data-sort-paragraph-option-id={optionId}
                className="w-full"
              >
                {/* Inner div owns HTML5 DnD so framer-motion does not swallow drag events */}
                <div
                  draggable={!disabled && !pointerDraggingRef.current}
                  onDragStart={(e) => {
                    if (disabled || pointerDraggingRef.current) {
                      e.preventDefault();
                      return;
                    }
                    draggingIdRef.current = optionId;
                    previewIndexRef.current = null;
                    setDraggingId(optionId);
                    setPreviewOrder(null);
                    e.dataTransfer.effectAllowed = 'move';
                    try {
                      e.dataTransfer.setData('text/plain', String(optionId));
                    } catch {
                      /* ignore */
                    }
                  }}
                  onDragEnd={() => {
                    if (pointerDraggingRef.current) return;
                    endDrag();
                  }}
                  onDragOver={(e) => {
                    if (disabled || draggingIdRef.current == null) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (optionId === draggingIdRef.current) return;
                    if (previewIndexRef.current !== idx) {
                      setPreviewToIndex(idx);
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (pointerDraggingRef.current) return;
                    endDrag();
                  }}
                  onPointerDown={(e) => {
                    if (disabled) return;
                    if (e.pointerType === 'mouse') return;
                    const target = e.target as HTMLElement;
                    if (target.closest('button')) return;
                    e.preventDefault();
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    pointerDraggingRef.current = true;
                    draggingIdRef.current = optionId;
                    previewIndexRef.current = null;
                    setDraggingId(optionId);
                    setPreviewOrder(null);
                    setTouchGhost({
                      optionId,
                      clientX: e.clientX,
                      clientY: e.clientY,
                      offsetX: e.clientX - rect.left,
                      offsetY: e.clientY - rect.top,
                      width: rect.width,
                      height: rect.height,
                      text,
                    });
                  }}
                  className={`w-full flex items-stretch gap-2 rounded-lg border px-3 py-2.5 transition-colors select-none touch-none ${
                    isDragging
                      ? 'opacity-60 border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-blue-400 dark:hover:border-blue-500'
                  } ${disabled ? 'opacity-70 cursor-default' : 'cursor-grab active:cursor-grabbing'}`}
                  style={{
                    touchAction: 'none',
                    visibility: isTouchGhosted ? 'hidden' : undefined,
                  }}
                >
                  <div className="flex flex-col justify-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      disabled={disabled || idx === 0 || draggingId != null}
                      onClick={() => moveBy(optionId, -1)}
                      className="p-1 rounded text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100 disabled:opacity-30"
                      aria-label="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      disabled={
                        disabled || idx === visualOrder.length - 1 || draggingId != null
                      }
                      onClick={() => moveBy(optionId, 1)}
                      className="p-1 rounded text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100 disabled:opacity-30"
                      aria-label="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  <p
                    className="flex-1 min-w-0 text-sm text-gray-900 dark:text-gray-100 leading-relaxed"
                    dir="auto"
                  >
                    {text}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
      {touchGhost && typeof document !== 'undefined'
        ? createPortal(
            <div
              style={{
                position: 'fixed',
                left: touchGhost.clientX - touchGhost.offsetX,
                top: touchGhost.clientY - touchGhost.offsetY,
                width: touchGhost.width,
                height: touchGhost.height,
                zIndex: 220,
                pointerEvents: 'none',
              }}
              className="rounded-lg border-2 border-blue-500 bg-white dark:bg-gray-800 px-3 py-2.5 shadow-lg opacity-95"
            >
              <p
                className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed line-clamp-4"
                dir="auto"
              >
                {touchGhost.text}
              </p>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
